import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FaDownload, 
  FaBuilding, 
  FaMapMarkerAlt,
  FaEnvelope,
  FaUser,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaSave
} from 'react-icons/fa';

// Styled Components with modern design
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 2rem;
  position: relative;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ExportButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: 0;
    right: 0;
    margin-top: 1rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  margin-top: 1rem;
  
  .progress {
    width: ${props => props.progress}%;
    height: 100%;
    background: white;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

const FormContainer = styled.div`
  padding: 2rem;
`;

const CompanyDetailsSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 2rem;
  overflow: hidden;
  border: 1px solid #dee2e6;
`;

const SectionHeader = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LenderRowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #0d6efd;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  color: #0d6efd;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
  }
  
  &::placeholder {
    color: #6c757d;
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: stretch;
  
  .input-icon {
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-right: none;
    border-radius: 12px 0 0 12px;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    color: #6c757d;
  }
  
  input {
    border-left: none;
    border-radius: 0 12px 12px 0;
  }
`;

const NextButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #0a58ca 0%, #1a73e8 100%);
    transform: translateY(-2px);
  }
`;

const ButtonContainer = styled.div`
  text-align: right;
`;

const MainContactContainer = styled.div`
  text-align: center;
  padding: 3rem 2rem;
`;

const ContactAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const ContactTitle = styled.h4`
  color: #212529;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ContactSubtitle = styled.p`
  color: #6c757d;
  margin-bottom: 2rem;
`;

const ContactFormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: white;
  color: #0d6efd;
  border: 2px solid #0d6efd;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #0d6efd;
    color: white;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #218838 0%, #1ea97c 100%);
    transform: translateY(-2px);
  }
`;

// EXACT ORIGINAL LOGIC PRESERVED - ONLY VISUAL CHANGES
const EditCompany = ({ companyData }) => {
  const [formData, setFormData] = useState(companyData);
  const [currentPage, setCurrentPage] = useState('editCompany');

  // EXACT ORIGINAL FUNCTION - NO CHANGES
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // EXACT ORIGINAL FUNCTION - NO CHANGES
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Updated company data:', formData);
  };

  // EXACT ORIGINAL FUNCTION - NO CHANGES
  const handleNext = () => {
    setCurrentPage('mainContact');
  };

  // EXACT ORIGINAL FUNCTION - NO CHANGES
  const handlePrevious = () => {
    setCurrentPage('editCompany');
  };

  // Main Contact Content (inline instead of separate component)
  if (currentPage === 'mainContact') {
    return (
      <Container>
        <Card>
          <HeaderSection>
            <Title>
              <FaEdit />
              Edit - Main Contact
            </Title>
            <ExportButton>
              <FaDownload />
              Export
            </ExportButton>
            <ProgressBar progress={100}>
              <div className="progress"></div>
            </ProgressBar>
          </HeaderSection>
          
          <MainContactContainer>
            <ContactAvatar>
              <FaUser size={32} color="white" />
            </ContactAvatar>
            <ContactTitle>Main Contact Information</ContactTitle>
            <ContactSubtitle>
              Configure the primary contact details for {formData?.name || 'this company'}
            </ContactSubtitle>
            
            <ContactFormContainer>
              <FormGrid>
                <FormGroup>
                  <Label>First Name</Label>
                  <Input type="text" placeholder="Enter first name" />
                </FormGroup>
                <FormGroup>
                  <Label>Last Name</Label>
                  <Input type="text" placeholder="Enter last name" />
                </FormGroup>
                <FormGroup>
                  <Label>Email</Label>
                  <InputGroup>
                    <div className="input-icon">
                      <FaEnvelope />
                    </div>
                    <Input type="email" placeholder="Enter email address" />
                  </InputGroup>
                </FormGroup>
                <FormGroup>
                  <Label>Phone</Label>
                  <Input type="tel" placeholder="Enter phone number" />
                </FormGroup>
              </FormGrid>
              
              <FormGroup style={{ marginBottom: '2rem' }}>
                <Label>Position</Label>
                <Input type="text" placeholder="Enter job title/position" />
              </FormGroup>

              <ButtonGroup>
                <BackButton onClick={handlePrevious}>
                  <FaArrowLeft />
                  Previous
                </BackButton>
                <SaveButton>
                  <FaSave />
                  Save Changes
                </SaveButton>
              </ButtonGroup>
            </ContactFormContainer>
          </MainContactContainer>
        </Card>
      </Container>
    );
  }

  // Main Edit Company Form
  return (
    <Container>
      <Card>
        <HeaderSection>
          <Title>
            <FaEdit />
            Edit
          </Title>
          <ExportButton>
            <FaDownload />
            Export
          </ExportButton>
          <ProgressBar progress={50}>
            <div className="progress"></div>
          </ProgressBar>
        </HeaderSection>

        <FormContainer>
          <form onSubmit={handleSubmit}>
            <CompanyDetailsSection>
              <SectionHeader>
                <SectionTitle>
                  <FaBuilding />
                  Company Details
                </SectionTitle>
                <DeleteButton>
                  <FaTrash />
                </DeleteButton>
              </SectionHeader>

              <FormContent>
                {/* First row - EXACT ORIGINAL LAYOUT */}
                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="araId">ARA ID</Label>
                    <Input
                      type="text"
                      id="araId"
                      name="araId"
                      value={formData?.id || ''}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="name">Company Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData?.name || ''}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </FormGrid>

                {/* Second row - EXACT ORIGINAL LAYOUT */}
                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="street">Street</Label>
                    <InputGroup>
                      <div className="input-icon">
                        <FaMapMarkerAlt />
                      </div>
                      <Input
                        type="text"
                        id="street"
                        name="street"
                        value={formData?.street || ''}
                        onChange={handleInputChange}
                      />
                    </InputGroup>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="street2">Street 2</Label>
                    <Input
                      type="text"
                      id="street2"
                      name="street2"
                      value={formData?.street2 || ''}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </FormGrid>

                {/* Third row - EXACT ORIGINAL 4-COLUMN LAYOUT */}
                <RowGrid>
                  <FormGroup>
                    <Label htmlFor="city">City</Label>
                    <Input
                      type="text"
                      id="city"
                      name="city"
                      value={formData?.city || ''}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="state">State</Label>
                    <Input
                      type="text"
                      id="state"
                      name="state"
                      value={formData?.state || ''}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="zipcode">Zipcode</Label>
                    <Input
                      type="text"
                      id="zipcode"
                      name="zipcode"
                      value={formData?.zipcode || ''}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="type">Type</Label>
                    <Input
                      type="text"
                      id="type"
                      name="type"
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </RowGrid>

                {/* Fourth row - EXACT ORIGINAL 2-COLUMN LENDER LAYOUT */}
                <LenderRowGrid>
                  <FormGroup>
                    <Label htmlFor="lenderName">Lender Name</Label>
                    <InputGroup>
                      <div className="input-icon">
                        <FaUser />
                      </div>
                      <Input
                        type="text"
                        id="lenderName"
                        name="lenderName"
                        onChange={handleInputChange}
                      />
                    </InputGroup>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="lenderEmail">Lender Email</Label>
                    <InputGroup>
                      <div className="input-icon">
                        <FaEnvelope />
                      </div>
                      <Input
                        type="email"
                        id="lenderEmail"
                        name="lenderEmail"
                        onChange={handleInputChange}
                      />
                    </InputGroup>
                  </FormGroup>
                </LenderRowGrid>

                {/* EXACT ORIGINAL BUTTON POSITIONING */}
                <ButtonContainer>
                  <NextButton type="button" onClick={handleNext}>
                    Next
                  </NextButton>
                </ButtonContainer>
              </FormContent>
            </CompanyDetailsSection>
          </form>
        </FormContainer>
      </Card>
    </Container>
  );
};

export default EditCompany;